import { useCallback, useEffect, useState } from 'react'
import { usePanel } from '../lib/panelContext'
import { useTrainerToken } from '../lib/trainerToken'

type Item = {
  id: string
  kind: 'rule' | 'example' | 'trigger'
  status: string
  title: string
  text?: string
  why?: string
  user?: string
  good?: string
  bad?: string
  signal?: string
  phrases?: string[]
  source?: string
}

type Call = { call_id: string; date: string; turns: number; flags: string[]; adjustments: string[] }

type FlowStep = { id: string; question: string; expected: string; handling: string; auth_first: boolean; updated: boolean }
type FlowSection = { section: string; page: number; updated: number; steps: FlowStep[] }
type Flow = { learning_mode: boolean; seeded_from: string; steps: number; updated: number; sections: FlowSection[] }
type Metrics = { recall_at_1: number; recall_at_3: number; recall_at_5: number; mrr: number; no_result_rate: number; unrelated_hits: number }
type RetrievalProposal = {
  id: string; status: string; reason: string; gates: string[]; candidate_config: Record<string, number>
  baseline_metrics: Metrics; candidate_metrics: Metrics
}
type Retrieval = { active: Record<string, number>; proposals: RetrievalProposal[]; history: unknown[]; bounds: Record<string, [number, number]> }
type KbPoint = {
  id: string; x: number; y: number; section: string; question: string; version: number
  requires_auth_first: boolean; has_pending_change: boolean
}
type KnowledgeMap = { counts: { visible_entries: number }; points: KbPoint[]; projection: { method: string } }
type Neighbor = { id: string; question: string; section: string; distance: number }

function detail(item: Item): string {
  if (item.kind === 'example') return `Caller: ${item.user}\nGood reply: ${item.good}${item.bad ? `\nAvoid: ${item.bad}` : ''}`
  if (item.kind === 'trigger') return `${item.signal}: ${(item.phrases ?? []).join(', ')}`
  return item.text ?? ''
}

export default function Trainer() {
  const { backendUrl } = usePanel()
  const { token, setToken } = useTrainerToken()
  const [items, setItems] = useState<Item[]>([])
  const [calls, setCalls] = useState<Call[]>([])
  const [flow, setFlow] = useState<Flow | null>(null)
  const [retrieval, setRetrieval] = useState<Retrieval | null>(null)
  const [knowledgeMap, setKnowledgeMap] = useState<KnowledgeMap | null>(null)
  const [selectedPoint, setSelectedPoint] = useState<KbPoint | null>(null)
  const [neighbors, setNeighbors] = useState<Neighbor[]>([])
  const [mapSearch, setMapSearch] = useState('')
  const [mapSection, setMapSection] = useState('')
  const [candidate, setCandidate] = useState({ max_distance: '', distance_margin: '', result_limit: '', candidate_pool: '' })
  const [openSection, setOpenSection] = useState(0)
  const [error, setError] = useState('')

  const api = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const resp = await fetch(`${backendUrl}${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', 'X-Trainer-Token': token.trim(), ...(init.headers ?? {}) },
      })
      if (!resp.ok) throw new Error(resp.status === 401 ? 'Invalid trainer token' : `Request failed (${resp.status})`)
      return resp.json()
    },
    [backendUrl, token],
  )

  const refresh = useCallback(async () => {
    if (!token.trim()) return
    try {
      const [i, c, f, r, m] = await Promise.all([
        api('/api/trainer/items'), api('/api/trainer/calls'), api('/api/trainer/flow'),
        api('/api/trainer/retrieval'), api('/api/trainer/kb/map'),
      ])
      setItems(i.items)
      setCalls(c.calls)
      setFlow(f)
      setRetrieval(r)
      setKnowledgeMap(m)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [api, token])

  useEffect(() => {
    void refresh()
    if (!token.trim()) return
    const timer = window.setInterval(() => void refresh(), 4000)
    return () => window.clearInterval(timer)
  }, [refresh, token])

  const act = async (path: string, body?: unknown) => {
    try {
      await api(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const pending = items.filter((i) => i.status === 'pending')
  const approved = items.filter((i) => i.status === 'approved' && i.source !== 'baseline')
  const flagged = calls.filter((c) => c.flags.length > 0)
  const section = flow?.sections[openSection]
  const sections = [...new Set(knowledgeMap?.points.map((point) => point.section) ?? [])].sort()
  const visiblePoints = (knowledgeMap?.points ?? []).filter((point) =>
    (!mapSection || point.section === mapSection) &&
    (!mapSearch || `${point.question} ${point.section}`.toLowerCase().includes(mapSearch.toLowerCase())),
  )
  const xValues = visiblePoints.map((point) => point.x)
  const yValues = visiblePoints.map((point) => point.y)
  const xMin = Math.min(...xValues, 0); const xMax = Math.max(...xValues, 1)
  const yMin = Math.min(...yValues, 0); const yMax = Math.max(...yValues, 1)
  const pointX = (point: KbPoint) => 18 + ((point.x - xMin) / (xMax - xMin || 1)) * 364
  const pointY = (point: KbPoint) => 182 - ((point.y - yMin) / (yMax - yMin || 1)) * 164

  const selectPoint = async (point: KbPoint) => {
    setSelectedPoint(point)
    try {
      const result = await api(`/api/trainer/kb/neighbors/${encodeURIComponent(point.id)}`)
      setNeighbors(result.neighbors)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const submitCandidate = async () => {
    const changes = Object.fromEntries(Object.entries(candidate)
      .filter(([, value]) => value !== '')
      .map(([key, value]) => [key, key === 'result_limit' || key === 'candidate_pool' ? Number.parseInt(value, 10) : Number(value)]))
    await act('/api/trainer/retrieval/proposals', { changes, reason: 'Trainer-requested bounded retrieval evaluation.' })
    setCandidate({ max_distance: '', distance_margin: '', result_limit: '', candidate_pool: '' })
  }

  return (
    <div className="page-body trainer-page">
      <header className="page-head">
        <h1>Call flow</h1>
        <p>Each block is a section of the FAQ test cases. A teal step was revised in a training chat.</p>
      </header>

      <label className="panel-field">
        <span>Trainer token</span>
        <input
          type="password"
          autoComplete="off"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Same token as Text chat → Train"
          data-testid="trainer-token"
        />
      </label>
      {error && <p className="trainer-error" role="alert">{error}</p>}

      {flow && section && (
        <section className="panel-section" data-testid="call-flow" aria-label="Call flow">
          <h2>Call flow · {flow.steps} test cases · {flow.updated} revised</h2>
          <p className="call-flow-note">
            {flow.learning_mode
              ? 'Learning mode is on. Train from Text chat, and a revised step shows here within a few seconds.'
              : 'Learning mode is off, so this diagram shows the test cases only.'}
          </p>
          <ol className="flow-rail">
            {flow.sections.map((item, index) => (
              <li key={item.section}>
                {index > 0 && <span className="flow-join" aria-hidden="true" />}
                <button
                  type="button"
                  className={`flow-node${index === openSection ? ' selected' : ''}${item.updated ? ' has-revision' : ''}`}
                  aria-pressed={index === openSection}
                  onClick={() => setOpenSection(index)}
                >
                  <span className="call-flow-index">{index + 1}</span>
                  <span className="flow-node-title">{item.section}</span>
                  <small>{item.steps.length}{item.updated ? ` · ${item.updated} revised` : ''}</small>
                </button>
              </li>
            ))}
          </ol>

          <div className="flow-detail">
            <h3>{section.section}</h3>
            <ol className="flow-steps">
              {section.steps.map((step, index) => (
                <li key={step.id} className={step.updated ? 'revised' : ''} data-testid={step.updated ? 'revised-step' : undefined}>
                  <span className="flow-step-index">{index + 1}</span>
                  <div>
                    <strong>{step.question}</strong>
                    {step.auth_first && <small>Authentication comes first</small>}
                    {step.updated ? (
                      <p><em>Revised handling.</em> {step.handling}</p>
                    ) : (
                      <p>{step.expected}</p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {retrieval && (
        <section className="panel-section retrieval-tuning" aria-label="Retrieval tuning">
          <h2>Retrieval tuning · v{retrieval.active.version}</h2>
          <p className="call-flow-note">
            Candidates are evaluated against the fixed FAQ suite. Only passing proposals can be approved; the agent cannot activate them.
          </p>
          <dl className="retrieval-active">
            {Object.entries(retrieval.active).filter(([key]) => key !== 'version').map(([key, value]) => (
              <div key={key}><dt>{key.replaceAll('_', ' ')}</dt><dd>{value}</dd></div>
            ))}
          </dl>
          <div className="retrieval-candidate">
            {Object.entries(retrieval.bounds).map(([key, [low, high]]) => (
              <label key={key}>
                <span>{key.replaceAll('_', ' ')} · {low}–{high}</span>
                <input
                  type="number"
                  min={low}
                  max={high}
                  step={key.includes('limit') || key.includes('pool') ? 1 : 0.01}
                  value={candidate[key as keyof typeof candidate]}
                  onChange={(event) => setCandidate((previous) => ({ ...previous, [key]: event.target.value }))}
                />
              </label>
            ))}
            <button type="button" className="primary" onClick={() => void submitCandidate()}>Evaluate candidate</button>
            {retrieval.history.length > 0 && <button type="button" className="warn" onClick={() => void act('/api/trainer/retrieval/rollback')}>Rollback active config</button>}
          </div>
          <ul className="trainer-list retrieval-proposals">
            {retrieval.proposals.map((proposal) => (
              <li key={proposal.id}>
                <strong>{proposal.status}: {proposal.id}</strong>
                <small>{proposal.reason || 'No stated reason'}</small>
                <p>
                  Recall@1 {proposal.baseline_metrics.recall_at_1} → {proposal.candidate_metrics.recall_at_1}
                  {' · '}Recall@3 {proposal.baseline_metrics.recall_at_3} → {proposal.candidate_metrics.recall_at_3}
                  {' · '}Unrelated hits {proposal.baseline_metrics.unrelated_hits} → {proposal.candidate_metrics.unrelated_hits}
                </p>
                {proposal.gates.length > 0 && <small className="trainer-error">{proposal.gates.join('; ')}</small>}
                {proposal.status === 'eligible' && (
                  <div className="trainer-actions">
                    <button type="button" className="primary" onClick={() => void act(`/api/trainer/retrieval/proposals/${proposal.id}/decision`, { approve: true })}>Approve config</button>
                    <button type="button" className="danger" onClick={() => void act(`/api/trainer/retrieval/proposals/${proposal.id}/decision`, { approve: false })}>Reject</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {knowledgeMap && (
        <section className="panel-section knowledge-map" aria-label="Knowledge map">
          <h2>Knowledge map · {knowledgeMap.counts.visible_entries} indexed questions</h2>
          <p className="call-flow-note">Exploratory 2D PCA of question embeddings. Proximity is not an answer-quality score.</p>
          <div className="knowledge-controls">
            <label>Search <input value={mapSearch} onChange={(event) => setMapSearch(event.target.value)} placeholder="Question or section" /></label>
            <label>Section <select value={mapSection} onChange={(event) => setMapSection(event.target.value)}><option value="">All sections</option>{sections.map((name) => <option key={name} value={name}>{name}</option>)}</select></label>
          </div>
          <div className="knowledge-layout">
            <svg className="knowledge-svg" viewBox="0 0 400 200" role="img" aria-label={`${visiblePoints.length} question embeddings projected with PCA`}>
              <line x1="18" y1="182" x2="382" y2="182" /><line x1="18" y1="18" x2="18" y2="182" />
              {visiblePoints.map((point) => (
                <circle
                  key={point.id} cx={pointX(point)} cy={pointY(point)} r={selectedPoint?.id === point.id ? 5 : 3}
                  className={`knowledge-point${point.requires_auth_first ? ' auth' : ''}${selectedPoint?.id === point.id ? ' selected' : ''}`}
                  tabIndex={0} role="button" aria-label={`${point.question}, ${point.section}`}
                  onClick={() => void selectPoint(point)}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); void selectPoint(point) } }}
                />
              ))}
            </svg>
            {selectedPoint && (
              <aside className="knowledge-detail">
                <strong>{selectedPoint.question}</strong>
                <small>{selectedPoint.section} · v{selectedPoint.version}{selectedPoint.requires_auth_first ? ' · authentication first' : ''}</small>
                <h3>Nearest questions</h3>
                <ol>{neighbors.map((neighbor) => <li key={neighbor.id}>{neighbor.question} <small>{neighbor.distance}</small></li>)}</ol>
              </aside>
            )}
          </div>
        </section>
      )}

      <details className="panel-section">
        <summary>Proposals and flagged calls ({pending.length + flagged.length})</summary>
        <section data-testid="pending-items">
          <h2>Awaiting your approval ({pending.length})</h2>
          {pending.length === 0 ? (
            <p>Nothing pending.</p>
          ) : (
            <ul className="trainer-list">
              {pending.map((i) => (
                <li key={i.id}>
                  <strong>{i.kind}: {i.title}</strong>
                  <pre>{detail(i)}</pre>
                  {i.why && <small>Why: {i.why}</small>}
                  <div className="trainer-actions">
                    <button type="button" className="primary" onClick={() => void act(`/api/trainer/items/${i.id}/decision`, { approve: true })}>Approve</button>
                    <button type="button" className="danger" onClick={() => void act(`/api/trainer/items/${i.id}/decision`, { approve: false })}>Reject</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h2>Flagged calls ({flagged.length})</h2>
          {flagged.length === 0 ? <p>None yet.</p> : (
            <ul className="trainer-list">
              {flagged.map((c) => (
                <li key={c.call_id}>
                  <code className="mono">{c.call_id}</code>
                  <small>{c.turns} turns · {c.flags.join(', ')}</small>
                </li>
              ))}
            </ul>
          )}
        </section>
        {approved.length > 0 && (
          <section>
            <h2>Trainer-approved rules ({approved.length})</h2>
            <ul className="trainer-list">
              {approved.map((i) => (
                <li key={i.id}>
                  <strong>{i.kind}: {i.title}</strong>
                  <button type="button" className="warn" onClick={() => void act(`/api/trainer/items/${i.id}/retire`)}>Retire</button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </details>
    </div>
  )
}
